import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Alert from '@/components/base/Alert';
import { adminApi } from '@/lib/adminApi';
import type { AdminIpBlock, AdminIpEntry, AdminIpHistory } from '@/lib/adminApi';
import { formatDate } from '@/lib/format';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, Modal, TableCell, TableHeaderCell } from './ui';

export default function IpTab({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [ips, setIps] = useState<AdminIpEntry[]>([]);
  const [blocks, setBlocks] = useState<AdminIpBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [target, setTarget] = useState<AdminIpEntry | null>(null);
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState('24');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState<AdminIpHistory[]>([]);

  const [manualIp, setManualIp] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [manualHours, setManualHours] = useState('24');
  const [manualBusy, setManualBusy] = useState(false);
  const [manualError, setManualError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await adminApi.ipsList(token);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setIps((res.ips ?? []) as AdminIpEntry[]);
      setBlocks((res.blocks ?? []) as AdminIpBlock[]);
      setHistory((res.history ?? []) as AdminIpHistory[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const block = async () => {
    if (!target || busy) return;
    setBusy(true);
    const res = await adminApi.ipBlock(token, {
      ip: target.ip,
      reason: reason.trim(),
      hours: Number(hours || 0),
      country: target.country ?? undefined,
    });
    setBusy(false);
    if (res.ok) {
      setMessage(t('admin.ip.blockedDone'));
      setTarget(null);
      setReason('');
      load();
    }
  };

  const unblock = async (blockId: number) => {
    setBusy(true);
    const res = await adminApi.ipUnblock(token, blockId);
    setBusy(false);
    if (res.ok) {
      setMessage(t('admin.ip.unblockedDone'));
      load();
    }
  };

  const isBlocked = (ip: string) =>
    blocks.some(
      (row) => row.ip === ip && row.active && (!row.blocked_until || new Date(row.blocked_until).getTime() > Date.now()),
    );

  const blockManual = async () => {
    if (manualBusy) return;
    setManualError('');
    setMessage('');
    const ip = manualIp.trim();
    if (!/^[0-9a-fA-F.:]+$/.test(ip) || ip.length < 4) {
      setManualError(t('admin.ip.invalidIp'));
      return;
    }
    setManualBusy(true);
    const res = await adminApi.ipBlock(token, {
      ip,
      reason: manualReason.trim() || t('admin.ip.manualDefaultReason'),
      hours: Number(manualHours || 0),
      manual: true,
    });
    setManualBusy(false);
    if (!res.ok) {
      setManualError(
        res.code === 'INVALID_IP'
          ? t('admin.ip.invalidIp')
          : res.code === 'INVALID_DURATION'
            ? t('admin.ip.invalidDuration')
            : t('errors.server'),
      );
      return;
    }
    setManualIp('');
    setManualReason('');
    setMessage(t('admin.ip.blockedDone'));
    load();
  };

  return (
    <div className="flex flex-col gap-5">
      {message ? <Alert tone="success">{message}</Alert> : null}

      <AdminCard title={t('admin.ip.manualTitle')} subtitle={t('admin.ip.manualSubtitle')}>
        <div className="flex flex-col gap-3.5">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1.4fr_120px_auto] gap-3 items-end">
            <Input
              label={t('admin.ip.address')}
              name="manualIp"
              value={manualIp}
              onChange={(event) => setManualIp(event.target.value)}
              placeholder="203.0.113.42"
              icon="ri-router-line"
              spellCheck={false}
            />
            <Input
              label={t('admin.ip.reason')}
              name="manualReason"
              value={manualReason}
              onChange={(event) => setManualReason(event.target.value)}
              placeholder={t('admin.ip.reasonPh')}
              icon="ri-file-text-line"
            />
            <Input
              label={t('admin.ip.hours')}
              name="manualHours"
              type="number"
              min="0"
              max="8760"
              value={manualHours}
              onChange={(event) => setManualHours(event.target.value)}
            />
            <Button
              loading={manualBusy}
              onClick={blockManual}
              icon={<i className="ri-forbid-2-line" aria-hidden="true" />}
              className="w-full lg:w-auto"
            >
              {t('admin.ip.block')}
            </Button>
          </div>
          {manualError ? <Alert tone="error">{manualError}</Alert> : null}
          <Alert tone="info">{t('admin.ip.blockWarning')}</Alert>
        </div>
      </AdminCard>

      <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_1fr] gap-5 items-start">
        <AdminCard
          title={t('admin.ip.title')}
          subtitle={t('admin.ip.subtitle')}
          actions={
            <span className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-100 px-2.5 py-1 text-[11px] font-semibold text-foreground-600 font-label whitespace-nowrap">
              <span className="flex items-center justify-center w-3 h-3 text-accent-600">
                <i className="ri-map-pin-line text-[11px]" aria-hidden="true" />
              </span>
              {t('admin.ip.note')}
            </span>
          }
          padded={false}
        >
          {loading ? (
            <LoadingBlock />
          ) : failed ? (
            <ErrorBlock onRetry={load} />
          ) : ips.length === 0 ? (
            <EmptyBlock label={t('admin.ip.emptyAttempts')} icon="ri-router-line" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[660px]">
                <thead className="bg-background-100">
                  <tr>
                    <TableHeaderCell>{t('admin.ip.address')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.ip.country')}</TableHeaderCell>
                    <TableHeaderCell align="right">{t('admin.ip.attempts')}</TableHeaderCell>
                    <TableHeaderCell align="right">{t('admin.ip.failures')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.ip.lastSeen')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.common.actions')}</TableHeaderCell>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {ips.map((row) => (
                    <tr key={row.ip} className="hover:bg-background-100/60 transition-colors">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-label text-[12.5px] text-foreground-900">{row.ip}</span>
                          {isBlocked(row.ip) ? (
                            <span className="inline-flex items-center rounded-full bg-accent-100 border border-accent-200 px-2 py-0.5 text-[10.5px] font-semibold text-accent-900 font-label whitespace-nowrap">
                              {t('admin.ip.blocked')}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-foreground-600">{row.country ?? '—'}</span>
                      </TableCell>
                      <TableCell align="right">{row.attempts}</TableCell>
                      <TableCell align="right">
                        <span className={row.failures > 0 ? 'text-accent-700 font-semibold' : 'text-foreground-600'}>
                          {row.failures}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-foreground-500 whitespace-nowrap">
                          {formatDate(row.lastSeen, i18n.language, true)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => {
                            setTarget(row);
                            setReason('');
                            setHours('24');
                          }}
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-foreground-700 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <span className="flex items-center justify-center w-3.5 h-3.5">
                            <i className="ri-forbid-line text-[13px]" aria-hidden="true" />
                          </span>
                          {t('admin.ip.block')}
                        </button>
                      </TableCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminCard>

        <AdminCard title={t('admin.ip.blockedTitle')} subtitle={t('admin.ip.blockedHint')} padded={false}>
          {loading ? (
            <LoadingBlock />
          ) : blocks.length === 0 ? (
            <EmptyBlock label={t('admin.ip.empty')} icon="ri-shield-check-line" />
          ) : (
            <ul className="divide-y divide-background-200">
              {blocks.map((row) => {
                const active = row.active && (!row.blocked_until || new Date(row.blocked_until).getTime() > Date.now());
                return (
                  <li key={row.id} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-label text-[12.5px] font-semibold text-foreground-950">{row.ip}</p>
                        <p className="mt-0.5 text-[11.5px] text-foreground-500 break-words">
                          {row.reason ?? '—'}
                          {row.country ? ` · ${row.country}` : ''}
                        </p>
                        <p className="mt-0.5 text-[11px] text-foreground-400">
                          {row.blocked_until
                            ? `${t('admin.ip.until')} ${formatDate(row.blocked_until, i18n.language, true)}`
                            : t('admin.ip.permanent')}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <span
                          className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10.5px] font-semibold font-label ${
                            active
                              ? 'bg-accent-100 text-accent-900 border-accent-200'
                              : 'bg-background-200 text-foreground-600 border-background-300'
                          }`}
                        >
                          {active ? t('admin.ip.stateActive') : t('admin.ip.stateLifted')}
                        </span>
                        {active ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => unblock(row.id)}
                            className="text-[11.5px] font-semibold text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            {t('admin.ip.unblock')}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </AdminCard>
      </div>

      <AdminCard title={t('admin.ip.historyTitle')} subtitle={t('admin.ip.historyHint')} padded={false}>
        {loading ? (
          <LoadingBlock />
        ) : history.length === 0 ? (
          <EmptyBlock label={t('admin.ip.historyEmpty')} icon="ri-history-line" />
        ) : (
          <ul className="divide-y divide-background-200 max-h-[420px] overflow-y-auto">
            {history.map((row) => {
              const lifted = row.action === 'security.ip_unblocked';
              return (
                <li key={row.id} className="px-5 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 font-label text-[12.5px] font-semibold text-foreground-950">
                      <span
                        className={`flex items-center justify-center w-3.5 h-3.5 ${
                          lifted ? 'text-primary-700' : 'text-accent-700'
                        }`}
                      >
                        <i
                          className={lifted ? 'ri-lock-unlock-line text-[13px]' : 'ri-lock-line text-[13px]'}
                          aria-hidden="true"
                        />
                      </span>
                      {row.ip ?? '—'}
                    </p>
                    <p className="mt-0.5 text-[11.5px] text-foreground-500 break-words">
                      {lifted ? t('admin.ip.historyUnblocked') : t('admin.ip.historyBlocked')}
                      {row.actorLabel ? ` · ${row.actorLabel}` : ''}
                    </p>
                  </div>
                  <span className="text-[11px] text-foreground-400 whitespace-nowrap shrink-0">
                    {formatDate(row.createdAt, i18n.language, true)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </AdminCard>

      <Modal
        open={Boolean(target)}
        title={t('admin.ip.blockTitle')}
        subtitle={target?.ip}
        onClose={() => setTarget(null)}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setTarget(null)}>
              {t('admin.common.cancel')}
            </Button>
            <Button size="sm" loading={busy} onClick={block}>
              {t('admin.ip.block')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <Alert tone="info">{t('admin.ip.blockWarning')}</Alert>
          <Input
            label={t('admin.ip.reason')}
            name="ipReason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('admin.ip.reasonPh')}
            icon="ri-file-text-line"
          />
          <Input
            label={t('admin.ip.hours')}
            name="ipHours"
            type="number"
            min="0"
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            hint={t('admin.ip.hoursHint')}
          />
        </div>
      </Modal>
    </div>
  );
}